<script>
	import { doc, getFirestore, writeBatch } from "firebase/firestore";
	import { getExtensionEnvironment, slugify } from "cando-util";

	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import { faCircleMinus } from "@fortawesome/free-solid-svg-icons";

	import { store } from "$lib/utilities/store";

	import Select from "$lib/components/Select.svelte";

	export let email;

	$: userExtensions = Object.keys($store.extensions?.docs || {})
		.map((eid) => $store.extensions.docs[eid])
		.filter(
			(e) =>
				e.type === "User" &&
				getExtensionEnvironment(e) === "Production",
		);

	$: filteredExtensionOptions = [
		{ option: "(Add Extension)", value: "(Add Extension)" },
		...userExtensions
			.filter((a) => !(a.authorizedEmails || []).includes(email))
			.map((a) => ({
				option: `${a.name} (Ext. ${a.extensionNumber})`,
				value: a.id.toString(),
			}))
			.sort((a, b) => (a.option < b.option ? -1 : 1)),
	];

	$: title = Object.keys($store.extensions?.docs || {})
		.map((eid) => $store.extensions.docs[eid])
		.filter(
			(e) =>
				e.type === "User" &&
				getExtensionEnvironment(e) === "Production" &&
				(e.authorizedEmails || []).includes(email),
		)
		.map((e) => e.extensionNumber)
		.join(", ");

	const updateEmails = async (emails, userExtensionId) => {
		const batch = writeBatch(getFirestore());
		const userExtensionUpdates = {
			authorizedEmails: emails,
		};
		batch.update(
			doc(getFirestore(), `extensions/${userExtensionId}`),
			userExtensionUpdates,
		);

		// Update the authorizedEmails now for each department
		// query extensions where this extension is a member of the queue
		// then update their authorizedEmails
		const departmentExtensionIds = [
			...new Set(
				Object.keys($store.extensions.docs)
					.map((eid) => $store.extensions.docs[eid])
					.filter((e) =>
						(
							(e.type === "Department" &&
								getExtensionEnvironment(e) === "Production" &&
								(e.queueMembers || [])) ||
							[]
						)
							.map((q) => q.id.toString())
							.includes(userExtensionId),
					)
					.map((e) => e.id.toString()),
			),
		];

		for (const departmentExtensionId of departmentExtensionIds) {
			const departmentExtensionUpdates = {
				authorizedEmails: [
					...new Set(
						(
							(
								$store.extensions.docs[departmentExtensionId]
									?.queueMembers || []
							).map((q) =>
								q.id.toString() === userExtensionId
									? emails // new authorizedEmails
									: ($store.extensions.docs[q.id.toString()]
											?.authorizedEmails ?? []),
							) || []
						).reduce((a, v) => [...a, ...v], []),
					),
				].sort(),
			};

			batch.update(
				doc(getFirestore(), `extensions/${departmentExtensionId}`),
				departmentExtensionUpdates,
			);
		}

		await batch.commit();
	};

	const addEmailToUserExtension = async (e, email) => {
		const extensionId = e.target.value;
		const emails =
			$store.extensions?.docs?.[extensionId]?.authorizedEmails || [];
		emails.push(email);
		await updateEmails(emails, extensionId);
		setTimeout(() => {
			e.target.value = "(Add Extension)";
		}, 1000);
	};

	const removeEmailFromUserExtension = async (extensionId, email) => {
		const emails =
			$store.extensions?.docs?.[extensionId]?.authorizedEmails || [];
		emails.splice(emails.indexOf(email), 1);
		await updateEmails(emails, extensionId);
		setTimeout(() => {
			const elemId = slugify(`Add Extension For ${email}`);
			const elem = document.getElementById(elemId);
			elem.value = "(Add Extension)";
		}, 1000);
	};
</script>

<div class="collapse collapse-arrow">
	<input type="checkbox" />
	<div class="collapse-title">{title}</div>
	<div class="collapse-content">
		{#each $store.extensions && userExtensions.sort( (a, b) => (`${a.name} (Ext. ${a.extensionNumber})` < `${b.name} (Ext. ${b.extensionNumber})` ? -1 : 1), ) as userExtension}
			{#if (userExtension?.authorizedEmails || []).includes(email)}
				<div class="flex justify-between mb-2">
					<div>
						{`${userExtension.name} (Ext. ${userExtension.extensionNumber})`}
					</div>
					<button
						class="text-error"
						on:click|preventDefault={() =>
							removeEmailFromUserExtension(
								userExtension.id.toString(),
								email,
							)}
					>
						<SvelteFa icon={faCircleMinus} size="1.5x" />
					</button>
				</div>
			{/if}
		{/each}
		<div>
			<Select
				name={`Add Extension For ${email}`}
				options={filteredExtensionOptions}
				handleChange={(e) => addEmailToUserExtension(e, email)}
			/>
		</div>
	</div>
</div>
